import React, { useState } from 'react';
import { API_BASE_URL } from '../../config/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Eye, Check, X, Clock, Loader2,
  MapPin, User, Video,
  ChevronDown, ChevronUp, Edit2, Archive, RotateCcw, Star, Trash2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { statusColors } from './constants';

export const QuoteCard = ({ quote, expanded, onToggle, onApprove, onReject, onRequestInspection, onEdit, onArchive, onDelete, onRestore, loading, showStatus, selected, onSelect }) => {
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  let aiAnalysis = null;
  try {
    aiAnalysis = quote.ai_analysis ? JSON.parse(quote.ai_analysis) : null;
  } catch (e) {
    console.warn('Failed to parse AI analysis JSON:', e);
  }

  return (
    <Card className={`border-border ${quote.highlighted ? 'ring-2 ring-yellow-400 bg-yellow-50/30' : ''}`} data-testid={`quote-${quote.id}`}>
      <CardContent className="p-4 md:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="flex-1 min-w-0 flex items-start gap-3">
            {onSelect && (
              <input 
                type="checkbox"
                checked={selected || false}
                onChange={() => onSelect(quote.id)}
                className="mt-1.5 h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer flex-shrink-0"
                data-testid={`select-quote-${quote.id}`}
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {quote.highlighted && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />}
                <h3 className="font-semibold text-base md:text-lg font-['Outfit'] truncate">{quote.service_name}</h3>
                {showStatus && <Badge className={`${statusColors[quote.status]} text-xs`}>{quote.status?.replace('_', ' ')}</Badge>}
              </div>
              <div className="flex flex-wrap gap-2 md:gap-4 text-xs md:text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3 md:w-4 md:h-4" /> {quote.user_name || 'Customer'}
                </span>
                <span className="flex items-center gap-1 truncate max-w-[150px] md:max-w-none">
                  <MapPin className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" /> {quote.address}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3 md:w-4 md:h-4" /> {format(parseISO(quote.created_at), 'MMM d, HH:mm')}
                </span>
              </div>
            </div>
          </div>
          
          {/* Price */}
          <div className="text-left sm:text-right flex-shrink-0">
            {quote.price_min && quote.price_max ? (
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">AI Estimate</p>
                <p className="text-lg md:text-xl font-bold text-primary font-['Outfit']">
                  £{quote.price_min?.toFixed(0)} - £{quote.price_max?.toFixed(0)}
                </p>
              </div>
            ) : (
              <p className="text-lg md:text-xl font-bold text-primary font-['Outfit']">
                £{quote.total_cost?.toFixed(2)}
              </p>
            )}
          </div>
        </div>

        {/* Expand Toggle */}
        <button 
          onClick={onToggle}
          className="flex items-center gap-1 text-xs md:text-sm text-primary mt-3 md:mt-4 hover:underline"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded ? 'Hide details' : 'View details'}
        </button>

        {/* Expanded Content */}
        {expanded && (
          <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-border space-y-4">
            {/* Customer Info */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Email</p>
                <p className="truncate text-sm">{quote.user_email}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Phone</p>
                <p className="text-sm">{quote.user_phone || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Urgency</p>
                <p className="capitalize text-sm">{quote.urgency}</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <p className="text-xs text-muted-foreground mb-1">Description</p>
              <p className="text-sm bg-muted p-2 md:p-3 rounded-lg">{quote.description}</p>
            </div>

            {/* Media */}
            {quote.media_urls?.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">
                  Uploaded Media ({quote.media_urls.length} files)
                </p>
                <div className="flex gap-2 flex-wrap">
                  {quote.media_urls.map((url, i) => {
                    const baseUrl = API_BASE_URL || '';
                    let mediaUrl = url;
                    if (!url.startsWith('http')) {
                      const cleanUrl = url.startsWith('/api') ? url : (url.startsWith('/uploads') ? `/api${url}` : `/api/uploads/${url}`);
                      mediaUrl = `${baseUrl}${cleanUrl}`;
                    }
                    
                    const isVideo = url.match(/\.(mp4|mov|webm)$/i);
                    
                    return (
                      <button 
                        key={url}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(mediaUrl, '_blank', 'noopener,noreferrer');
                        }}
                        className="w-16 h-16 md:w-20 md:h-20 rounded-lg bg-muted flex items-center justify-center hover:opacity-80 transition-opacity overflow-hidden border"
                      >
                        {isVideo ? (
                          <Video className="w-6 h-6 md:w-8 md:h-8 text-muted-foreground" />
                        ) : (
                          <img 
                            src={mediaUrl}
                            alt={`Media ${i + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = document.createElement('span');
                              fallback.className = 'text-xs text-muted-foreground';
                              fallback.textContent = 'Image unavailable';
                              e.target.parentElement.appendChild(fallback);
                            }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* AI Analysis */}
            {aiAnalysis && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                <h4 className="font-semibold text-blue-800 mb-2 text-sm">AI Analysis</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 text-xs md:text-sm">
                  <div>
                    <p className="text-blue-600">Complexity</p>
                    <p className="font-medium capitalize">{aiAnalysis.complexity}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Est. Hours</p>
                    <p className="font-medium">{aiAnalysis.estimated_hours_min}-{aiAnalysis.estimated_hours_max}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Confidence</p>
                    <p className={`font-medium ${
                      aiAnalysis.confidence === 'high' ? 'text-green-600' :
                      aiAnalysis.confidence === 'medium' ? 'text-yellow-600' : 'text-red-600'
                    }`}>{aiAnalysis.confidence}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Inspection</p>
                    <p className="font-medium">{aiAnalysis.needs_inspection ? 'Yes' : 'No'}</p>
                  </div>
                </div>
                {aiAnalysis.analysis && (
                  <p className="text-xs md:text-sm text-blue-700 mt-2">{aiAnalysis.analysis}</p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            {onApprove && (
              <div className="flex flex-wrap gap-2 pt-3 md:pt-4 border-t border-border">
                <Button
                  className="btn-accent text-xs md:text-sm"
                  size="sm"
                  onClick={onApprove}
                  disabled={loading}
                >
                  <Check className="w-3 h-3 md:w-4 md:h-4 mr-1" /> Approve
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onEdit}
                  disabled={loading}
                  className="text-xs md:text-sm"
                >
                  <Edit2 className="w-3 h-3 md:w-4 md:h-4 mr-1" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRequestInspection}
                  disabled={loading}
                  className="text-xs md:text-sm"
                >
                  <Eye className="w-3 h-3 md:w-4 md:h-4 mr-1" /> Inspect
                </Button>
                
                {!showRejectInput ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:bg-destructive/10 text-xs md:text-sm"
                    onClick={() => setShowRejectInput(true)}
                    disabled={loading}
                  >
                    <X className="w-3 h-3 md:w-4 md:h-4 mr-1" /> Reject
                  </Button>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2 w-full mt-2">
                    <Input
                      placeholder="Reason for rejection..."
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="flex-1 text-sm"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => { onReject(rejectReason); setShowRejectInput(false); }}
                        disabled={loading}
                        className="text-xs md:text-sm"
                      >
                        Confirm
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setShowRejectInput(false)} className="text-xs md:text-sm">
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Archive/Delete/Restore Buttons */}
            <div className="flex flex-wrap gap-2 pt-3 md:pt-4 border-t border-border mt-3">
              {quote.status === 'archived' ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onRestore}
                    disabled={loading}
                    className="text-xs md:text-sm"
                  >
                    <RotateCcw className="w-3 h-3 md:w-4 md:h-4 mr-1" /> Restore
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onDelete}
                    disabled={loading}
                    className="text-destructive hover:bg-destructive/10 text-xs md:text-sm"
                  >
                    <Trash2 className="w-3 h-3 md:w-4 md:h-4 mr-1" /> Delete Forever
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onArchive}
                    disabled={loading}
                    className="text-xs md:text-sm text-muted-foreground"
                  >
                    <Archive className="w-3 h-3 md:w-4 md:h-4 mr-1" /> Archive
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onDelete}
                    disabled={loading}
                    className="text-destructive hover:bg-destructive/10 text-xs md:text-sm"
                  >
                    <Trash2 className="w-3 h-3 md:w-4 md:h-4 mr-1" /> Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
